import { ChangeEvent, ReactElement, useEffect, useState } from 'react'
import LoadingView from 'ui/molecules/LoadingView'
import Stack from 'syf-component-library/ui/spacing/Stack/Stack'
import { Inline } from 'syf-component-library/ui/spacing'
import { i18n } from 'strings/i18n'
import { HOME_PAGE } from 'const/config'
import { getMFEConfigForMockState } from 'const/configMFE'
import {
  HELPER_TEXT_ERROR_ALIGNMENT,
  INPUT_FORMATS,
  PAYLATER_AUTH_FLOW
} from 'const/constants'
import {
  useAuthSession,
  useAuthSignIn,
  useMockAuth,
  useQualtrics,
  useSettings,
  useUser
} from 'hooks'
import { useTestQuery } from 'services/testQuery'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import { analyticsEvents } from 'helpers/analytics/events'
import { isAccountNotFoundError } from 'helpers/isAccountNotFoundError'
import { getPageSubfunction } from 'helpers/analytics/helpers'
import getCorrelationId from 'helpers/getCorrelationId'
import FullWidthButton from 'ui/atoms/FullWidthButton'
import NoAccountFoundMessageBox from 'ui/molecules/NoAccountMsgBox'
import { INPUTS } from 'const/inputsIds'
import { ICONS } from 'const/icons'
import en from 'strings/lang/en'
import { setAnalyticsConfigDefaultProp } from 'helpers/analytics/SFDDLHelpers'
import { PAGE_INFO_PROPS } from 'helpers/analytics/analytics.types'
import { PAYLATER_DEFAULT_PRODUCT } from 'const/analytics'
import type { MobileErrors, SSNErrors } from 'strings/lang/translations.types'
import { MaskedInput } from 'ui/atoms/MaskedInput/MaskedInput'
import {
  getInputFieldError,
  getPhoneError,
  isFormValid
} from './PaylaterSignInUtils'
import { getSSNError } from '../SecuredInstallmentsSignIn/SecuredInstallmentsUtils'
import {
  SigninCardContainer,
  SigninCardContent,
  SigninHeader
} from './PaylaterSignIn.styles'

const PaylaterSignIn = (): ReactElement => {
  const [showLoadingView, setShowLoadingView] = useState(false)
  const [isNoAccountFound, setIsNoAccountFound] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [lastFourSSN, setLastFourSSN] = useState('')
  const [phoneError, setPhoneError] = useState<MobileErrors | ''>('')
  const [ssnError, setSsnError] = useState<SSNErrors | ''>('')
  const { mockAuthIfMocking } = useMockAuth()
  const { currentLanguage } = useUser()
  const { isMockDataEnabled, forceApiError } = useSettings()
  // fetches and saves the session id
  const { introspectPromise } = useAuthSession()
  const { reloadQualtrics } = useQualtrics()

  // for testing api calls on click
  const { refetch } = useTestQuery({
    config: getMFEConfigForMockState(isMockDataEnabled),
    error: forceApiError
  })

  const {
    isErrorAuthenticate,
    authenticateErrorData,
    authenticateSignin,
    completeSignin,
    authorizeUserAndRedirect
  } = useAuthSignIn(PAYLATER_AUTH_FLOW)

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value)
    setPhoneError('')
  }

  const handleSSNChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLastFourSSN(e.target.value)
    setSsnError('')
  }

  const validatePhone = () => {
    setPhoneError(getPhoneError(phoneNumber))
  }

  const validateSsn = () => {
    setSsnError(getSSNError(lastFourSSN))
  }

  const handleSignin = async (event) => {
    event.preventDefault()
    setShowLoadingView(true)

    await introspectPromise

    try {
      if (!isFormValid(phoneNumber, lastFourSSN)) {
        validatePhone()
        validateSsn()
        if (isNoAccountFound) setIsNoAccountFound(false)
        setShowLoadingView(false)
        return
      }

      if (forceApiError) {
        setShowLoadingView(false)
        await refetch()
        return
      }

      // authenticate user's signin credentials at the identity provider
      await authenticateSignin({
        ssn: lastFourSSN,
        phone: phoneNumber
      })

      // tell oauth user authenticated and to authorize app, undefined passed for ts compiler reasons
      const completeResponse = await completeSignin(undefined)

      // msw doesn't intercept the form submit action since it's a document request
      if (!isMockDataEnabled) {
        // relay identity provider result to the auth server to authorize requesting an access token
        authorizeUserAndRedirect({
          completeResponse,
          language: currentLanguage
        })
      }

      mockAuthIfMocking()
    } catch (error) {
      setShowLoadingView(false)
      throw error
    }
  }

  useEffect(() => {
    setIsNoAccountFound(
      isAccountNotFoundError(isErrorAuthenticate, authenticateErrorData)
    )
  }, [isErrorAuthenticate, authenticateErrorData])

  useEffect(() => {
    if (isNoAccountFound) {
      setPhoneNumber('')
      setPhoneError('mobileEmptyError')
      setLastFourSSN('')
      setSsnError('ssnEmptyError')

      const errorMessage = en.paylaterSignIn.incorrectCredentials
      triggerAnalyticsEvent(
        analyticsEvents.signInError(
          'pay-later-login-error',
          errorMessage,
          getPageSubfunction(PAYLATER_AUTH_FLOW)
        )
      )
    }
  }, [isNoAccountFound])

  useEffect(() => {
    // Allows back to go to homepage since the history gets cleared by oauth flow
    const originalURL = window.location.href
    const homepageURLSlashed = `${HOME_PAGE}/`
    const { history } = window
    history.pushState(history.state, '', homepageURLSlashed)
    // we need to re-add the original url to maintain history after modifying it
    history.pushState(history.state, '', originalURL)

    setAnalyticsConfigDefaultProp(PAGE_INFO_PROPS.Token, getCorrelationId())
    setAnalyticsConfigDefaultProp(
      PAGE_INFO_PROPS.Product,
      PAYLATER_DEFAULT_PRODUCT
    )

    triggerAnalyticsEvent(
      analyticsEvents.signinLoad(
        getCorrelationId(),
        getPageSubfunction(PAYLATER_AUTH_FLOW)
      )
    )
    reloadQualtrics()
  }, [])

  useEffect(() => {
    reloadQualtrics()
  }, [currentLanguage])

  return showLoadingView ? (
    <LoadingView showText={false} />
  ) : (
    <SigninCardContainer>
      <SigninCardContent>
        <SigninHeader>{i18n({ paylaterSignIn: 'title' })}</SigninHeader>
        <Stack size="base" />
        {isNoAccountFound && (
          <NoAccountFoundMessageBox
            bodyText={i18n({ paylaterSignIn: 'incorrectCredentials' })}
          />
        )}
        <Stack size="base" />
        <form onSubmit={handleSignin}>
          <MaskedInput
            data-private
            defaultUnmaskedValue={phoneNumber}
            error={getInputFieldError(phoneError)}
            errorIcon={ICONS.SOLID_EXCLAMATION_TRIANGLE}
            errorOrHelpClassName={HELPER_TEXT_ERROR_ALIGNMENT}
            helpText={i18n({ paylaterSignIn: 'mobileHelper' })}
            id={INPUTS.MOBILE_NUMBER}
            inputMode="numeric"
            leftIcon={ICONS.MOBILE}
            mask={INPUT_FORMATS.PHONE_NUMBER}
            onChange={handlePhoneChange}
            onBlur={validatePhone}
            placeholder={i18n({ paylaterSignIn: 'mobilePlaceholder' })}
            type="tel"
            width="100%"
          />
          <Stack size="base" />
          <MaskedInput
            data-private
            defaultUnmaskedValue={lastFourSSN}
            error={getInputFieldError(ssnError)}
            errorIcon={ICONS.SOLID_EXCLAMATION_TRIANGLE}
            errorOrHelpClassName={HELPER_TEXT_ERROR_ALIGNMENT}
            helpText={i18n({ paylaterSignIn: 'ssnHelpText' })}
            id={INPUTS.LAST_FOUR_SSN}
            inputMode="numeric"
            leftIcon={ICONS.LOCK}
            mask={INPUT_FORMATS.SSN}
            onChange={handleSSNChange}
            onBlur={validateSsn}
            placeholder={i18n({ paylaterSignIn: 'ssnPlaceholder' })}
            type="text"
            width="100%"
          />
          <Stack size="xlarge" />
          <FullWidthButton
            buttonType="primary"
            type="submit"
            onClick={handleSignin}
            data-reason="signin"
            data-type="secure signin"
            data-analytics="clicktrackingevent"
          >
            <Inline size="medium" />
            {i18n({ paylaterSignIn: 'signInBtn' })}
          </FullWidthButton>
        </form>
      </SigninCardContent>
    </SigninCardContainer>
  )
}

export default PaylaterSignIn
